import React from "react"
import { Keyboard, LayoutChangeEvent, Platform, View, ViewStyle } from "react-native"

import { observer } from "mobx-react-lite"
import { TMessage } from "types/transcript"

import { useChat } from "src/context/ChatContext"
import { useMediaPip } from "src/context/MediaPipContext"
import { useSidekickInteraction } from "src/context/SidekickInteractionContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { isAndroid } from "src/utils/platform"
import { ChatTextInput } from "./ChatTextInput"

interface TypingChatInputDockProps {
  /**
   * Called whenever the dock's rendered height changes (and with `0` when
   * the dock is hidden). Useful for callers that need to keep an overlay or
   * gesture-capture region clear of the dock — for example, MediaPlayerShell
   * uses it to shrink the tap-to-toggle overlay so taps on the input/send
   * button are not intercepted as media play/pause taps.
   */
  onHeightChange?: (height: number) => void
}

/**
 * TypingChatInputDock - A reusable dock that renders a ChatTextInput when the
 * user activates typing mode (e.g. by pressing the Type button anywhere in the
 * app).
 *
 * Listens for the `shouldActivateTyping` flag from MediaPipContext, manages
 * its own visibility against the platform keyboard lifecycle, and routes sent
 * messages through the active chat's `sendMessage`.
 *
 * Mount this once on any screen that should support inline typing (e.g. via
 * SidekickView). Place it at the bottom of the screen's content column so it
 * appears just above the platform keyboard when active.
 */
export const TypingChatInputDock = observer(function TypingChatInputDock({
  onHeightChange,
}: TypingChatInputDockProps) {
  const { themed } = useAppTheme()
  const { sendMessage } = useChat()
  const { shouldActivateTyping, acknowledgeTypingActivation } = useMediaPip()
  const { inputMode, returnToIdle } = useSidekickInteraction()
  // Mirror inputMode in a ref so keyboard-event handlers (registered once)
  // always see the latest mode without re-binding listeners every render.
  const inputModeRef = React.useRef(inputMode)
  React.useEffect(() => {
    inputModeRef.current = inputMode
  }, [inputMode])

  // Bumping the key when typing is re-activated forces a remount so autoFocus
  // re-runs even if the input was already on screen but had been blurred.
  const [textInputKey, setTextInputKey] = React.useState(0)
  const [showTextInput, setShowTextInput] = React.useState(false)

  React.useEffect(() => {
    if (shouldActivateTyping > 0) {
      setShowTextInput(true)
      setTextInputKey((k) => k + 1)
      acknowledgeTypingActivation()
    }
  }, [shouldActivateTyping, acknowledgeTypingActivation])

  // On web Keyboard.dismiss() doesn't fire keyboard events, so fall back to
  // tracking the input mode to hide the dock when the user switches to talk.
  React.useEffect(() => {
    if (Platform.OS === "web" && inputMode !== "type") {
      setShowTextInput(false)
    }
  }, [inputMode])

  // Debounce the hide so a quick remount (e.g. textInputKey bump) doesn't
  // briefly drop the dock between the old input unmounting and the new one
  // grabbing focus.
  const keyboardHideTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(() => {
    function handleKeyboardShow() {
      if (keyboardHideTimeoutRef.current) {
        clearTimeout(keyboardHideTimeoutRef.current)
        keyboardHideTimeoutRef.current = null
      }
      setShowTextInput(true)
    }

    function handleKeyboardHide() {
      if (keyboardHideTimeoutRef.current) {
        clearTimeout(keyboardHideTimeoutRef.current)
      }
      keyboardHideTimeoutRef.current = setTimeout(() => {
        setShowTextInput(false)
        // Keep inputMode in sync with the dock's visibility so that pressing
        // Type after the user dismisses the keyboard (tap-outside) re-activates
        // typing instead of being interpreted as a toggle-off.
        if (inputModeRef.current === "type") {
          returnToIdle()
        }
      }, 100)
    }

    if (isAndroid) {
      const showSubscription = Keyboard.addListener("keyboardDidShow", handleKeyboardShow)
      const hideSubscription = Keyboard.addListener("keyboardDidHide", handleKeyboardHide)
      return () => {
        showSubscription.remove()
        hideSubscription.remove()
        if (keyboardHideTimeoutRef.current) clearTimeout(keyboardHideTimeoutRef.current)
      }
    }

    const showSubscription = Keyboard.addListener("keyboardWillShow", handleKeyboardShow)
    const hideSubscription = Keyboard.addListener("keyboardWillHide", handleKeyboardHide)
    return () => {
      showSubscription.remove()
      hideSubscription.remove()
      if (keyboardHideTimeoutRef.current) clearTimeout(keyboardHideTimeoutRef.current)
    }
  }, [returnToIdle])

  // Report 0 height back to the caller as soon as the dock unmounts. (When it
  // mounts, `handleLayout` reports the actual measured height.)
  React.useEffect(() => {
    if (!showTextInput) {
      onHeightChange?.(0)
    }
  }, [showTextInput, onHeightChange])

  const handleLayout = React.useCallback(
    (e: LayoutChangeEvent) => {
      onHeightChange?.(e.nativeEvent.layout.height)
    },
    [onHeightChange],
  )

  const handleSend = React.useCallback(
    (message: TMessage) => {
      if (!message.text) return
      sendMessage(message.text)
      // Cancel any pending debounced hide so the dock doesn't briefly flicker
      // back on between the explicit hide here and the keyboard-dismiss event.
      if (keyboardHideTimeoutRef.current) {
        clearTimeout(keyboardHideTimeoutRef.current)
        keyboardHideTimeoutRef.current = null
      }
      Keyboard.dismiss()
      setShowTextInput(false)
      returnToIdle()
    },
    [sendMessage, returnToIdle],
  )

  if (!showTextInput) return null

  return (
    <View style={themed($inputContainer)} onLayout={handleLayout}>
      <ChatTextInput key={textInputKey} onSend={handleSend} />
    </View>
  )
})

const $inputContainer: ThemedStyle<ViewStyle> = ({ spacing, colors }) => ({
  borderTopColor: colors.inputBorder,
  borderTopWidth: 1,
  paddingTop: spacing.xxs,
  paddingBottom: 0,
  backgroundColor: colors.background,
})
